import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useParallax } from '../../../hooks/useParallax';

export default function Benefits() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  
  // Parallax effects
  const parallax1 = useParallax({ speed: 0.4, direction: 'down' });
  const parallax2 = useParallax({ speed: 0.3, direction: 'up' });

  const scrollToContact = () => {
    const element = document.getElementById('contato');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Pagamento apenas pelas horas realmente trabalhadas',
      description: 'Elimine custos desnecessários com horas não trabalhadas',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Controle real de presença e jornada, mesmo em áreas remotas',
      description: 'Saiba exatamente onde e quando cada colaborador está trabalhando',
      color: 'from-teal-500 to-cyan-500'
    },
    {
      icon: 'ri-time-line',
      title: 'Redução imediata de horas extras indevidas',
      description: 'Registros precisos que eliminam horas extras sem justificativa',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: 'ri-team-line',
      title: 'Menos discussões com equipe, mais clareza para o gestor',
      description: 'Transparência total que reduz conflitos e aumenta a confiança',
      color: 'from-blue-500 to-indigo-500'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Fechamento de folha mais rápido e previsível',
      description: 'Dados organizados que agilizam o processo de pagamento',
      color: 'from-indigo-500 to-purple-500'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Tranquilidade em fiscalizações e processos trabalhistas',
      description: 'Documentação completa e válida juridicamente',
      color: 'from-purple-500 to-pink-500'
    }
  ];

  return (
    <section 
      ref={ref}
      id="beneficios" 
      className={`py-20 lg:py-28 bg-gradient-to-br from-white via-teal-50 to-cyan-50 relative overflow-hidden transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
    >
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute top-1/4 right-0 w-96 h-96 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${parallax1}px)` }}
        ></div>
        <div 
          className="absolute bottom-1/4 left-0 w-96 h-96 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full blur-3xl animate-pulse" 
          style={{ animationDelay: '1s', transform: `translateY(${parallax2}px)` }}
        ></div>
      </div>

      {/* Padrão decorativo */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="absolute top-10 left-10 w-32 h-32 border-4 border-teal-500 rounded-full"
          style={{ transform: `translateY(${parallax1 * 0.5}px)` }}
        ></div>
        <div 
          className="absolute top-1/3 right-20 w-24 h-24 border-4 border-cyan-500 rounded-lg rotate-45"
          style={{ transform: `translateY(${parallax2 * 0.7}px) rotate(45deg)` }}
        ></div>
        <div 
          className="absolute bottom-20 left-1/3 w-40 h-40 border-4 border-emerald-500 rounded-full"
          style={{ transform: `translateY(${parallax1 * 0.6}px)` }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 animate-bounce">
            <span className="relative bg-gradient-to-r from-emerald-600 via-teal-500 to-cyan-600 text-white px-8 py-3 rounded-full text-sm font-bold shadow-2xl inline-flex items-center gap-2 transform hover:scale-110 transition-all duration-300 cursor-pointer group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-teal-500 to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute inset-0 animate-pulse bg-white/20 rounded-full"></div>
              <i className="ri-gift-line text-lg relative z-10 group-hover:rotate-12 transition-transform duration-300"></i>
              <span className="relative z-10">Vantagens Reais</span>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full"></div>
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Benefícios práticos para a operação rural
          </h2>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            O ponto deixa de ser um problema constante e passa a simplesmente funcionar.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-3 cursor-pointer overflow-hidden border-2 border-transparent hover:border-teal-200"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${benefit.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
              
              <div className="relative z-10">
                <div className={`w-16 h-16 bg-gradient-to-br ${benefit.color} rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-xl`}>
                  <i className={`${benefit.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug group-hover:text-teal-600 transition-colors">
                  {benefit.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>

              <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${benefit.color} opacity-10 rounded-full transform translate-x-10 -translate-y-10 group-hover:scale-150 transition-transform duration-500`}></div>
            </div>
          ))}
        </div>

        <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-12 group border-4 border-white">
          <img
            src="https://static.readdy.ai/image/2fb69e05e600ee45b7cfaa2e2ddcddf8/03451e40763f3879741c6b79f6d6025b.jpeg"
            alt="Equipe organizada no campo"
            className="w-full h-64 sm:h-80 lg:h-96 object-cover object-center transform group-hover:scale-110 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/90 via-teal-900/50 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-8 text-white">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-line-chart-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-2xl md:text-3xl font-bold leading-tight">Transforme a gestão de sua equipe rural</h3>
            </div>
            <p className="text-sm sm:text-base lg:text-lg text-white/95">Mais eficiência, menos custos, total controle</p>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            onClick={scrollToContact}
            className="group bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white px-6 sm:px-10 py-3 sm:py-4 rounded-lg text-sm sm:text-lg font-semibold hover:from-emerald-700 hover:via-teal-700 hover:to-cyan-700 transition-all duration-300 whitespace-nowrap transform hover:scale-105 hover:shadow-2xl border-2 border-white/20"
          >
            <span className="flex items-center justify-center gap-2">
              Converse com um especialista
              <i className="ri-arrow-right-line text-lg sm:text-xl group-hover:translate-x-1 transition-transform"></i>
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}